/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import moment from 'moment'

import type { CloudResource } from '@modules/billing-api/v1/types'

import type { Budget, Costs } from '../types'

export const getCurrentMonthDates = () => ({
  from: moment.utc().startOf('month').format(),
  to: moment.utc().endOf('day').format(),
})

interface GetBalanceDataParams {
  budget: Budget
  costs: Costs | undefined
  cloudResources: CloudResource[] | undefined
}

interface GetBalanceDataResponse {
  current: number
  max: number
}

export const getBalanceData = ({
  budget,
  costs,
  cloudResources,
}: GetBalanceDataParams): GetBalanceDataResponse => {
  const { amount: budgetAmount, scope_type: scopeType } = budget

  let usage: number

  switch (scopeType) {
    case 'organization':
      usage = costs?.total_ecu || 0
      break
    case 'cloud_resource':
      const { scope_values: scopeInstances } = budget

      // Create a Map from CloudResource ID to cluster_id for O(1) lookups
      const cloudResourceMap = new Map(
        cloudResources?.map((resource) => [resource.id, resource.cluster_id]) || [],
      )

      // Map scopeInstances (CloudResource IDs) to cluster_ids using the Map
      const clusterIdsSet = new Set<string>()

      for (const scopeInstanceId of scopeInstances) {
        const clusterId = cloudResourceMap.get(scopeInstanceId)

        if (clusterId) {
          clusterIdsSet.add(clusterId)
        }
      }

      const instances = costs?.instances.filter(({ id }) => clusterIdsSet.has(id)) || []
      usage = instances.reduce((sum, instance) => sum + instance.total_ecu, 0)
      break
    default:
      usage = 0
  }

  return { current: usage, max: budgetAmount }
}

export const getAlertThresholds = (
  alerts?: Array<Pick<Budget['alerts'][number], 'threshold' | 'threshold_type'>>,
): string[] => {
  if (!alerts?.length) {
    return []
  }

  const thresholds = alerts.map(
    ({ threshold, threshold_type: thresholdType }) =>
      `${threshold}${thresholdType.toLocaleLowerCase() === 'percentage' ? '%' : ' ECU'}`,
  )

  return thresholds
}

interface BalanceComparatorParams {
  a: Budget
  b: Budget
  cloudResources: CloudResource[] | undefined
  costs: Costs | undefined
  sortDirection: 'asc' | 'desc'
}

export const balanceComparator = ({
  a,
  b,
  costs,
  cloudResources,
  sortDirection,
}: BalanceComparatorParams) => {
  const balanceA = getBalanceData({ budget: a, costs, cloudResources })
  const balanceB = getBalanceData({ budget: b, costs, cloudResources })

  const percentA = ((balanceA.current || 0) / (balanceA.max || 1)) * 100
  const percentB = ((balanceB.current || 0) / (balanceB.max || 1)) * 100

  const comparison = percentA - percentB

  return sortDirection === 'asc' ? comparison : -comparison
}

export const scopeComparator = (a: Budget, b: Budget, sortDirection: 'asc' | 'desc') => {
  const scopeTypeCompare = a.scope_type.localeCompare(b.scope_type)

  if (scopeTypeCompare !== 0) {
    return sortDirection === 'asc' ? scopeTypeCompare : -scopeTypeCompare
  }

  const aScopeValue = a.scope_values[0]?.toLowerCase() || ''
  const bScopeValue = b.scope_values[0]?.toLowerCase() || ''
  const scopeValueCompare = aScopeValue.localeCompare(bScopeValue)
  return sortDirection === 'asc' ? scopeValueCompare : -scopeValueCompare
}
